import { Card } from '@/components/ui/card';
import { TrendingUp, MapPin, DollarSign } from 'lucide-react';

const HotMarkets = () => {
  // Market data - median prices and growth rates are illustrative of DFW trends
  const markets = [
    {
      city: 'Frisco',
      county: 'Collin County',
      median_price: 585000,
      growth: '+8.2%',
      days_on_market: 28,
      highlight: 'Fastest Growing',
      color: 'red'
    },
    {
      city: 'Plano',
      county: 'Collin County',
      median_price: 475000,
      growth: '+5.1%',
      days_on_market: 32,
      highlight: 'Most Popular',
      color: 'blue'
    },
    {
      city: 'McKinney',
      county: 'Collin County',
      median_price: 425000,
      growth: '+6.8%',
      days_on_market: 35,
      highlight: 'Hot Market',
      color: 'orange'
    },
    {
      city: 'Arlington',
      county: 'Tarrant County',
      median_price: 315000,
      growth: '+4.2%',
      days_on_market: 38,
      highlight: 'Best Value',
      color: 'green'
    },
    {
      city: 'Mansfield',
      county: 'Tarrant County',
      median_price: 385000,
      growth: '+7.3%',
      days_on_market: 30,
      highlight: 'Family Favorite',
      color: 'purple'
    },
    {
      city: 'Allen',
      county: 'Collin County',
      median_price: 495000,
      growth: '+5.9%',
      days_on_market: 29,
      highlight: 'Top Schools',
      color: 'indigo'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      red: 'from-red-500 to-red-600 border-red-500',
      blue: 'from-blue-500 to-blue-600 border-blue-500',
      orange: 'from-orange-500 to-orange-600 border-orange-500',
      green: 'from-green-500 to-green-600 border-green-500',
      purple: 'from-purple-500 to-purple-600 border-purple-500',
      indigo: 'from-indigo-500 to-indigo-600 border-indigo-500'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="my-12">
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
          🔥 Hot Real Estate Markets in Dallas-Fort Worth
        </h2>
        <p className="text-lg text-gray-600">
          Discover the fastest-growing Texas neighborhoods and best FSBO opportunities in DFW
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {markets.map((market, idx) => (
          <Card 
            key={idx} 
            className={`relative overflow-hidden hover:shadow-2xl transition-all duration-300 border-l-4 ${getColorClasses(market.color).split(' ')[2]}`}
          >
            {/* Highlight Badge */}
            <div className={`absolute top-0 right-0 bg-gradient-to-r ${getColorClasses(market.color).split(' ').slice(0, 2).join(' ')} text-white text-xs font-bold px-3 py-1 rounded-bl-lg`}>
              {market.highlight}
            </div>

            <div className="p-6">
              {/* City Name */}
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{market.city}</h3>
                <p className="text-sm text-gray-500 flex items-center">
                  <MapPin className="w-3 h-3 mr-1" />
                  {market.county}
                </p>
              </div>

              {/* Stats Grid */}
              <div className="space-y-3">
                {/* Median Price */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center">
                    <DollarSign className="w-4 h-4 mr-1" />
                    Median Price
                  </span>
                  <span className="font-bold text-gray-900">
                    ${market.median_price.toLocaleString()}
                  </span>
                </div>

                {/* Growth */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    Growth (YoY)
                  </span>
                  <span className="font-bold text-green-600">{market.growth}</span>
                </div>

                {/* Days on Market */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Avg Days on Market</span>
                  <span className="font-semibold text-gray-700">{market.days_on_market} days</span>
                </div>

                {/* FREE Listing CTA */}
                <div className="flex items-center justify-between bg-green-50 rounded-lg p-2 -mx-2">
                  <span className="text-sm text-green-700 font-medium">List Here FREE</span>
                  <span className="font-bold text-green-600">$0 Fee!</span>
                </div>
              </div>

              {/* View Listings Button */}
              <button 
                onClick={() => window.location.href = `/search?city=${market.city}`}
                className={`w-full mt-4 bg-gradient-to-r ${getColorClasses(market.color).split(' ').slice(0, 2).join(' ')} text-white py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity`}
              >
                View Listings
              </button>
            </div>
          </Card>
        ))}
      </div>

      {/* DFW Landmarks & Western Culture */}
      <div className="mt-12 bg-gradient-to-r from-amber-50 via-orange-50 to-red-50 rounded-xl p-8 border-4 border-orange-300 relative overflow-hidden">
        {/* Western Pattern Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="text-9xl">🤠</div>
        </div>
        
        <div className="relative z-10">
          <h3 className="text-3xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Playfair Display' }}>
            🤠 Authentic Texas Living in DFW
          </h3>
          <p className="text-center text-gray-600 mb-6">Where Big D meets Cowtown - The Best of Both Worlds!</p>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {/* Sports */}
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-orange-200">
              <div className="text-4xl mb-2">🏈</div>
              <p className="text-sm font-bold text-gray-900">AT&T Stadium</p>
              <p className="text-xs text-gray-600">Dallas Cowboys</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-blue-200">
              <div className="text-4xl mb-2">⚾</div>
              <p className="text-sm font-bold text-gray-900">Globe Life Field</p>
              <p className="text-xs text-gray-600">Texas Rangers</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-blue-200">
              <div className="text-4xl mb-2">🏀</div>
              <p className="text-sm font-bold text-gray-900">AA Center</p>
              <p className="text-xs text-gray-600">Dallas Mavericks</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-green-200">
              <div className="text-4xl mb-2">🎢</div>
              <p className="text-sm font-bold text-gray-900">Six Flags</p>
              <p className="text-xs text-gray-600">Over Texas</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-purple-200">
              <div className="text-4xl mb-2">🌆</div>
              <p className="text-sm font-bold text-gray-900">Dallas Skyline</p>
              <p className="text-xs text-gray-600">Downtown</p>
            </div>
          </div>

          {/* Western Culture Section */}
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg p-6 border-2 border-orange-400">
            <h4 className="text-2xl font-bold text-center mb-4 text-orange-900">
              🐂 Fort Worth - Where the West Begins
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-2">🐄</div>
                <p className="text-sm font-bold text-gray-900">Stockyards</p>
                <p className="text-xs text-gray-600">Historic District</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-2">🎸</div>
                <p className="text-sm font-bold text-gray-900">Billy Bob's Texas</p>
                <p className="text-xs text-gray-600">World's Largest Honky Tonk</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-2">🤠</div>
                <p className="text-sm font-bold text-gray-900">Cowboy Hats</p>
                <p className="text-xs text-gray-600">& Boots Culture</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-2">🐎</div>
                <p className="text-sm font-bold text-gray-900">Rodeos</p>
                <p className="text-xs text-gray-600">Year-Round Events</p>
              </div>
            </div>
          </div>

          {/* Texas Pride Banner */}
          <div className="mt-6 text-center">
            <p className="text-2xl font-bold text-orange-800">
              🌟 Y'all Come Back Now! 🌟
            </p>
            <p className="text-sm text-gray-700 mt-2">
              From downtown Dallas skyscrapers to Fort Worth's western heritage - 
              <br />
              <span className="font-semibold">Find Your Perfect Texas Home Today!</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotMarkets;
